import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { downloadCsv } from '../lib/download';
import { money, shortDate } from '../lib/format';
import type { Analytics, MoneyByCurrency } from '../types';
import { Button, Card, ErrorNotice, LoadingScreen, PageHeader } from '../components/ui';

/** Готовые периоды: за произвольными датами художник идёт в выгрузку. */
const PERIODS = [
  { days: 30, label: '30 дней' },
  { days: 90, label: '90 дней' },
  { days: 365, label: 'Год' },
];

const EXPORTS = [
  { entity: 'orders', label: 'Заказы' },
  { entity: 'payments', label: 'Платежи' },
  { entity: 'clients', label: 'Клиенты' },
];

/**
 * Отчёт художника. Всё считает сервер из журнала платежей и заказов — здесь
 * только показ: две правды об одних и тех же деньгах не нужны никому.
 */
export function AnalyticsPage() {
  const [days, setDays] = useState(90);
  const from = isoDaysAgo(days);

  const report = useQuery({
    queryKey: ['analytics', days],
    queryFn: () => api<Analytics>(`/artist/analytics?from=${from}`),
  });

  if (report.isPending) return <LoadingScreen label="Считаем отчёт" />;
  if (report.error) return <ErrorNotice error={report.error} />;

  const data = report.data;

  return (
    <>
      <PageHeader eyebrow="Отчёт" title="Деньги и работа" />

      <div className="chip-row">
        {PERIODS.map((period) => (
          <button
            key={period.days}
            className={`chip ${period.days === days ? 'is-active' : ''}`}
            onClick={() => setDays(period.days)}
          >
            {period.label}
          </button>
        ))}
      </div>
      <p className="meta">
        {shortDate(data.from)} — {shortDate(data.to)}
      </p>

      <div className="section-heading">
        <h2>Деньги</h2>
      </div>
      <Card className="stack stack--compact">
        <MoneyRow label="Получено" money={data.revenue} />
        <MoneyRow label="Возвраты" money={data.refunds} />
        <MoneyRow label="Итого" money={data.net} strong />
        <MoneyRow label="Ждём оплаты" money={data.outstanding} />
        <p className="muted settings-note">
          Задолженность — на сегодня по активным заказам. Валюты не складываются.
        </p>
      </Card>

      <div className="section-heading">
        <h2>Работа</h2>
      </div>
      <Card className="stack stack--compact">
        <div className="fact-row">
          <span className="meta">Завершено заказов</span>
          <strong>{data.completedOrders}</strong>
        </div>
        <MoneyRow label="Средний чек" money={data.averageCheck} />
        <div className="fact-row">
          <span className="meta">Средняя длительность</span>
          <strong>{data.averageDays === null ? '—' : `${data.averageDays} дн.`}</strong>
        </div>
        <div className="fact-row">
          <span className="meta">Сдано с опозданием</span>
          <strong>{data.overdueShare === null ? '—' : `${data.overdueShare}%`}</strong>
        </div>
      </Card>

      <div className="section-heading">
        <h2>Заявки</h2>
      </div>
      <Card className="stack stack--compact">
        <div className="fact-row">
          <span className="meta">Пришло</span>
          <strong>{data.applications.total}</strong>
        </div>
        <div className="fact-row">
          <span className="meta">Стали заказами</span>
          <strong>
            {data.applications.accepted}
            {data.applications.conversion !== null && ` · ${data.applications.conversion}%`}
          </strong>
        </div>
        <div className="fact-row">
          <span className="meta">Отклонено</span>
          <strong>{data.applications.rejected}</strong>
        </div>
        {data.applications.rejected > 0 && (
          <p className="muted settings-note">
            С пояснением: {data.applications.rejectedWithNote} из {data.applications.rejected}.
          </p>
        )}
        {data.applications.pending > 0 && (
          <div className="fact-row">
            <span className="meta">Ждут ответа</span>
            <strong>{data.applications.pending}</strong>
          </div>
        )}
      </Card>

      {data.byService.length > 0 && (
        <>
          <div className="section-heading">
            <h2>По услугам</h2>
          </div>
          <Card className="stack stack--compact">
            {data.byService.map((service) => (
              <div key={service.serviceId ?? 'none'} className="fact-row">
                <span>
                  {service.title}
                  <span className="meta"> · {service.orders}</span>
                </span>
                <strong>{formatMoney(service.money)}</strong>
              </div>
            ))}
          </Card>
        </>
      )}

      <div className="section-heading">
        <h2>Выгрузка</h2>
      </div>
      <Card className="stack stack--compact">
        <div className="export-actions">
          {EXPORTS.map((item) => (
            <ExportButton key={item.entity} entity={item.entity} label={item.label} from={from} />
          ))}
        </div>
        <p className="muted settings-note">
          CSV для Excel и Google Sheets. Клиенты выгружаются целиком, без периода.
        </p>
      </Card>
    </>
  );
}

function MoneyRow({
  label,
  money: amounts,
  strong,
}: {
  label: string;
  money: MoneyByCurrency[];
  strong?: boolean;
}) {
  return (
    <div className="fact-row">
      <span className="meta">{label}</span>
      {strong ? <strong>{formatMoney(amounts)}</strong> : <span>{formatMoney(amounts)}</span>}
    </div>
  );
}

/**
 * Кнопка, а не ссылка: выгрузка закрыта сессией, а к обычной ссылке заголовок
 * авторизации не прикрепить. Файл скачивается запросом и отдаётся браузеру уже
 * из памяти.
 */
function ExportButton({ entity, label, from }: { entity: string; label: string; from: string }) {
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  const download = async () => {
    setPending(true);
    setFailed(false);

    try {
      await downloadCsv(
        `/v1/artist/analytics/export?entity=${entity}&from=${from}`,
        `artflow-${entity}.csv`,
      );
    } catch {
      setFailed(true);
    } finally {
      setPending(false);
    }
  };

  return (
    <Button variant="secondary" disabled={pending} onClick={() => void download()}>
      {pending ? 'Собираем…' : failed ? 'Повторить' : label}
    </Button>
  );
}

/** Разные валюты остаются разными строками — сложить их нам нечем. */
function formatMoney(amounts: MoneyByCurrency[]): string {
  if (amounts.length === 0) return '—';

  return amounts.map((item) => money(item.amount, item.currency)).join(' · ');
}

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}
